import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './DocumentFolder.module.scss';
import itemStyles from '../ItemsList/ItemsList.module.scss';
import { ReactComponent as ArrowDownIcon } from 'app/assets/Icons/ArrowDown.svg';
import { OptionsButton } from '../OptionsButton/OptionsButton';
import { DocumentItem } from '../DocumentItem/DocumentItem';
import { IDocumentFolder } from './DocumentFolder.interface';
import { InlineEditor } from '../InlineEditor/InlineEditor';
import {
  useAppSelector,
  useAppDispatch,
  getSelectedDocuments,
  getCanSelectDocuments,
  setSelectedDocuments
} from 'app/redux';
import { toggleMultipleDocuments } from 'app/utils';
import { Tooltip } from 'react-tooltip';

export const DocumentFolder: React.FC<IDocumentFolder> = ({
  folderName,
  documents,
  collapseTrigger,
  editingDocumentPath,
  editingFolderName,
  documentActions,
  folderActions
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useAppDispatch();
  const selectedDocuments = useAppSelector(getSelectedDocuments);
  const canSelectDocuments = useAppSelector(getCanSelectDocuments);
  const hasSelectedDocuments = selectedDocuments.length > 0;
  const maxDisplayLength = 25;
  const shouldShowTooltip = folderName.length > maxDisplayLength;

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };
  const isEditingFolder = editingFolderName === folderName;

  const folderDocumentPaths = documents.map((doc) => `${folderName}/${doc}`);
  const selectedInFolder = folderDocumentPaths.filter((path) =>
    selectedDocuments.includes(path)
  );
  const isAllSelected =
    selectedInFolder.length === folderDocumentPaths.length &&
    folderDocumentPaths.length > 0;

  const handleFolderCheckboxChange = (): void => {
    if (!canSelectDocuments) return;
    const newSelection = toggleMultipleDocuments(
      selectedDocuments,
      folderDocumentPaths
    );
    dispatch(setSelectedDocuments(newSelection));
  };

  const getOptionsVariant = (): 'folder' | 'folder-with-selection' => {
    return hasSelectedDocuments ? 'folder-with-selection' : 'folder';
  };

  const handleMoveSelectedToThisFolder = (): void => {
    console.log('Moving selected documents to folder:', folderName);
    for (const documentPath of selectedDocuments) {
      documentActions.onMoveOrCopy?.(documentPath, folderName, false);
    }
  };

  const handleCopySelectedToThisFolder = (): void => {
    console.log('Copying selected documents to folder:', folderName);
    for (const documentPath of selectedDocuments) {
      documentActions.onMoveOrCopy?.(documentPath, folderName, true);
    }
  };

  useEffect(() => {
    if (collapseTrigger > 0) {
      setIsExpanded(false);
    }
  }, [collapseTrigger]);

  return (
    <div className={styles.container}>
      <div className={itemStyles['folder-item']}>
        <label className={itemStyles['folder-checkbox']}>
          <input
            type="checkbox"
            value={folderName}
            checked={isAllSelected}
            onChange={handleFolderCheckboxChange}
            disabled={!canSelectDocuments}
          />
          <span className={itemStyles.checkmark}></span>
          {isEditingFolder ? (
            <InlineEditor
              value={folderName}
              onSave={(newName) =>
                folderActions.onRename?.(folderName, newName)
              }
              onCancel={folderActions.onCancelEditing || (() => {})}
              placeholder={folderName}
            />
          ) : (
            <button
              className={styles['folder-toggle']}
              onClick={toggleExpanded}
              aria-expanded={isExpanded}
            >
              <ArrowDownIcon
                className={classnames(styles['toggle-icon'], {
                  [styles.expanded]: isExpanded
                })}
              />

              <span
                className={classnames({
                  [styles['shortened-title']]: shouldShowTooltip
                })}
                {...(shouldShowTooltip && {
                  'data-tooltip-id': 'folder-name-tooltip',
                  'data-tooltip-content': folderName
                })}
              >
                {shouldShowTooltip
                  ? `${folderName.slice(0, maxDisplayLength)}...`
                  : folderName}
              </span>
            </button>
          )}
        </label>

        <OptionsButton
          variant={getOptionsVariant()}
          onEdit={() => folderActions.onEdit?.(folderName)}
          onDelete={() => folderActions.onDelete?.(folderName)}
          onMove={handleMoveSelectedToThisFolder}
          onCopy={handleCopySelectedToThisFolder}
          onUpload={folderActions.onUpload}
        />

        <Tooltip
          id="folder-name-tooltip"
          className={itemStyles['shortened-title-tooltip']}
          noArrow
        />
      </div>

      <div
        className={classnames(styles.content, {
          [styles.expanded]: isExpanded
        })}
      >
        <div className={styles.inner}>
          {documents.map((document) => {
            const documentPath = `${folderName}/${document}`;
            return (
              <DocumentItem
                key={documentPath}
                documentName={document}
                documentPath={documentPath}
                isEditing={editingDocumentPath === documentPath}
                documentActions={documentActions}
                maxDisplayLength={20}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
